/**
 * Editing an artifact mints a new artifact row, so the id changes while the
 * thing on screen does not — it is the same document, one version on. Only
 * the chat frames carry the file lineage that says so (an artifact row's own
 * file ids are a different namespace), so this collects them into one stable
 * key per lineage.
 *
 * That key is what a context marker is keyed on: without it, every edit reads
 * as a switch to a different artifact and re-announces the one already open.
 */

import type { AgentChatArtifact } from '@/services/agents/chat-stream';

export function createArtifactLineage() {
  const keyByRoot = new Map<string, string>();
  const keyByTitle = new Map<string, string>();
  const keyByArtifact = new Map<string, string>();

  function keyFor(artifactId: string): string {
    return keyByArtifact.get(artifactId) ?? artifactId;
  }

  function record(frame: AgentChatArtifact): string | undefined {
    const artifactId = frame.artifact_id;
    if (!artifactId) return undefined;

    // A first version names no root: it is its own.
    const root = frame.root_file_id || frame.file_id || undefined;
    // The marker shows the name, so two artifacts that share one within a
    // conversation read as the same subject whatever the file chain says --
    // and a regenerated file arrives with no chain at all.
    const title = frame.title?.trim() || frame.filename?.trim() || undefined;
    const key =
      (root ? keyByRoot.get(root) : undefined) ??
      (title ? keyByTitle.get(title) : undefined) ??
      artifactId;

    if (root) keyByRoot.set(root, key);
    if (title && !keyByTitle.has(title)) keyByTitle.set(title, key);
    keyByArtifact.set(artifactId, key);
    return key;
  }

  return { record, keyFor };
}

export type ArtifactLineage = ReturnType<typeof createArtifactLineage>;
